import React, { useEffect, useMemo, useState } from 'react';
import { DatabaseNewsEntry, DatabaseNewsEntryModel, NewsEntryKind } from '../../services/DatabaseNewsEntry';
import { DatabaseSettingsModel } from '../../services/DatabaseSettings';
import { useSoundPacks } from '../../context/SoundPacksContext';
import { compareVersions } from '../../helpers/compareVersions';
import { isoToDatetimeLocal, datetimeLocalToIso } from '../../helpers/datetimeLocal';
import { formatShortDate } from '../../helpers/formatShortDate';
import { resizeImageToJpeg } from '../../helpers/resizeImage';

// Card images render at 122×72 in the plugin; 320px wide covers Retina.
const IMAGE_MAX_WIDTH = 320;
const IMAGE_JPEG_QUALITY = 0.82;

const KIND_LABELS: Record<NewsEntryKind, string> = {
    pack: 'Sound pack',
    update: 'Software update',
    other: 'Other',
};

// Holds every kind's fields, so switching kind while creating an entry doesn't
// lose what was typed; entryFromForm keeps only the chosen kind's. The kind of
// an existing entry can't be changed.
type NewsForm = {
    kind: NewsEntryKind;
    title: string;
    body: string;
    blurb: string;
    packId: string; // pack
    version: string; // update
    changelog: string; // update; one change per line
    createdAt: string; // other; datetime-local value
    link: string; // other; optional
    public: boolean;
    imageUrl: string | null; // currently stored image; null once removed
    imageBlob: Blob | null; // newly selected image, already resized
};

function formFromEntry(entry: DatabaseNewsEntry | null): NewsForm {
    return {
        kind: entry?.kind ?? 'pack',
        title: entry?.title ?? '',
        body: entry?.body ?? '',
        blurb: entry?.blurb ?? '',
        packId: entry?.kind === 'pack' ? entry.packId : '',
        version: entry?.kind === 'update' ? entry.version : '',
        changelog: entry?.kind === 'update' ? entry.changelog.join('\n') : '',
        createdAt: isoToDatetimeLocal(entry?.kind === 'other' ? entry.createdAt : new Date().toISOString()),
        link: entry?.kind === 'other' ? entry.link ?? '' : '',
        public: entry?.public ?? false,
        imageUrl: entry?.imageUrl ?? null,
        imageBlob: null,
    };
}

// Returns the first problem that blocks saving, or null when the form is valid.
function validate(form: NewsForm): string | null {
    if (!form.title.trim()) return 'Title is required.';
    switch (form.kind) {
        case 'pack':
            return form.packId ? null : 'Pick the sound pack this entry is about.';
        case 'update':
            return form.version ? null : 'Pick the released version this update is about.';
        case 'other':
            if (!datetimeLocalToIso(form.createdAt)) return 'Invalid date.';
            if (form.link.trim() && !/^https?:\/\/\S+$/i.test(form.link.trim())) return 'Link must be a full http(s) URL.';
            return null;
    }
}

function entryFromForm(form: NewsForm): DatabaseNewsEntry {
    const base = {
        title: form.title.trim(),
        public: form.public,
        body: form.body.trim() || null,
        blurb: form.blurb.trim() || null,
        imageUrl: form.imageUrl,
    };
    switch (form.kind) {
        case 'pack':
            return { ...base, kind: 'pack', packId: form.packId };
        case 'update':
            return { ...base, kind: 'update', version: form.version, changelog: form.changelog.split('\n').map(l => l.trim()).filter(Boolean) };
        case 'other':
            return { ...base, kind: 'other', createdAt: datetimeLocalToIso(form.createdAt) ?? '', link: form.link.trim() || null };
    }
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';

function NewsEditor() {
    const { soundPacks } = useSoundPacks();
    const [entries, setEntries] = useState<DatabaseNewsEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    // The entry being edited: an id, 'new' for a fresh entry, or null (list only).
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<NewsForm>(() => formFromEntry(null));
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [releaseDates, setReleaseDates] = useState<Record<string, string>>({});

    useEffect(() => {
        const unsub = DatabaseSettingsModel.subscribeVersions(
            (versions) => setReleaseDates(versions.releaseDates),
        );
        return () => unsub();
    }, []);

    useEffect(() => {
        const unsub = DatabaseNewsEntryModel.subscribeNewsEntries(
            true,
            (next) => {
                setEntries(next);
                setLoading(false);
            },
            (err) => {
                setLoadError(err.message);
                setLoading(false);
            },
        );
        return () => unsub();
    }, []);

    // Object URL for the freshly selected image, revoked when it changes.
    useEffect(() => {
        if (!form.imageBlob) {
            setImagePreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(form.imageBlob);
        setImagePreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [form.imageBlob]);

    const editingEntry = editingId && editingId !== 'new' ? entries.find(e => e.id === editingId) ?? null : null;
    const packName = (packId: string) => soundPacks.find(p => p.id === packId)?.name ?? packId;
    const linkedPack = form.kind === 'pack' ? soundPacks.find(p => p.id === form.packId) : undefined;
    const validationError = validate(form);
    const releasedVersions = useMemo(
        () => Object.keys(releaseDates).sort((a, b) => compareVersions(b, a)),
        [releaseDates],
    );
    // Pack and update entries take their date from the pack / release.
    const inheritedDate = form.kind !== 'other'
        ? DatabaseNewsEntryModel.resolveDate(entryFromForm(form), soundPacks, releaseDates)
        : null;
    // Newest-first by resolved date; undated entries (hidden in the plugin) on top.
    const listedEntries = useMemo(
        () => entries
            .map(entry => ({ entry, date: DatabaseNewsEntryModel.resolveDate(entry, soundPacks, releaseDates) }))
            .sort((a, b) => (b.date ?? '\uffff').localeCompare(a.date ?? '\uffff')),
        [entries, soundPacks, releaseDates],
    );

    const set = <K extends keyof NewsForm>(key: K, value: NewsForm[K]) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const startEditing = (entry: DatabaseNewsEntry | null) => {
        setEditingId(entry?.id ?? 'new');
        setForm(formFromEntry(entry));
    };

    const handleImageChange = async (file: File | null) => {
        if (!file) return;
        try {
            set('imageBlob', await resizeImageToJpeg(file, IMAGE_MAX_WIDTH, IMAGE_JPEG_QUALITY));
        } catch (error) {
            alert('Could not read that image: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    };

    const handleSave = async () => {
        if (validationError) return;
        setSaving(true);
        try {
            const entry = entryFromForm(form);
            let entryId = editingEntry?.id;
            if (!entryId) {
                entryId = await DatabaseNewsEntryModel.createNewsEntry(entry);
            }
            if (form.imageBlob) {
                entry.imageUrl = await DatabaseNewsEntryModel.uploadImage(entryId, form.imageBlob);
            } else if (!form.imageUrl && editingEntry?.imageUrl) {
                await DatabaseNewsEntryModel.deleteImage(entryId);
            }
            await DatabaseNewsEntryModel.updateNewsEntry(entryId, entry);
            setEditingId(null);
        } catch (error) {
            console.error('Error saving news entry:', error);
            alert('Failed to save: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (entry: DatabaseNewsEntry) => {
        if (!entry.id || !window.confirm(`Delete "${entry.title.split('\n')[0]}"? This can't be undone.`)) return;
        try {
            await DatabaseNewsEntryModel.deleteNewsEntry(entry.id);
            if (editingId === entry.id) setEditingId(null);
        } catch (error) {
            console.error('Error deleting news entry:', error);
            alert('Failed to delete: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (loadError) {
        return <p className="text-red-600 text-sm">Failed to load news: {loadError}</p>;
    }

    const shownImage = imagePreviewUrl ?? form.imageUrl;

    return (
        <div className="space-y-6">
            {editingId === null ? (
                <button
                    onClick={() => startEditing(null)}
                    className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                >
                    New entry
                </button>
            ) : (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {editingEntry ? 'Edit entry' : 'New entry'}
                    </h3>

                    <div>
                        <label className={labelClass}>Kind{editingEntry && ' (fixed — delete and recreate the entry to change it)'}</label>
                        <select value={form.kind} onChange={(e) => set('kind', e.target.value as NewsEntryKind)} className={inputClass} disabled={saving || !!editingEntry}>
                            {(Object.keys(KIND_LABELS) as NewsEntryKind[]).map(kind => (
                                <option key={kind} value={kind}>{KIND_LABELS[kind]}</option>
                            ))}
                        </select>
                    </div>

                    {form.kind === 'pack' && (
                        <div>
                            <label className={labelClass}>Sound pack (clicking the card opens its page)</label>
                            <select value={form.packId} onChange={(e) => set('packId', e.target.value)} className={inputClass} disabled={saving}>
                                <option value="">Select a pack…</option>
                                {soundPacks.map(pack => (
                                    <option key={pack.id} value={pack.id}>
                                        {pack.name}{pack.public ? '' : ' (preview)'}
                                    </option>
                                ))}
                            </select>
                            {form.public && linkedPack && !linkedPack.public && (
                                <p className="text-red-600 font-medium text-xs mt-1">
                                    ⚠️ This pack is still a preview — regular users won't see this entry until the pack is public.
                                </p>
                            )}
                        </div>
                    )}

                    {form.kind === 'update' && (
                        <div>
                            <label className={labelClass}>Version (users on an older plugin see "Update available"; clicking opens the website's account page)</label>
                            <select value={form.version} onChange={(e) => set('version', e.target.value)} className={inputClass} disabled={saving}>
                                <option value="">Select a released version…</option>
                                {releasedVersions.map(version => (
                                    <option key={version} value={version}>
                                        {version} (released {formatShortDate(releaseDates[version])})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className={labelClass}>Title (a new line becomes a line break; 2 lines max)</label>
                        <textarea value={form.title} onChange={(e) => set('title', e.target.value)} rows={2} placeholder={'Lofi Tapes\nOut Now!'} className={inputClass} disabled={saving} />
                    </div>

                    <div>
                        <label className={labelClass}>
                            Date (shown on the card, controls ordering and the "New" badge)
                            {form.kind === 'pack' ? ' — from the pack' : form.kind === 'update' ? ' — from the release' : ''}
                        </label>
                        {form.kind === 'other' ? (
                            <input type="datetime-local" value={form.createdAt} onChange={(e) => set('createdAt', e.target.value)} className={inputClass} disabled={saving} />
                        ) : (
                            <input type="datetime-local" value={isoToDatetimeLocal(inheritedDate)} className={inputClass} disabled />
                        )}
                    </div>

                    <div>
                        <label className={labelClass}>Second line (optional; muted text under the title)</label>
                        <input type="text" value={form.body} onChange={(e) => set('body', e.target.value)} placeholder="Version 2.1 is here." className={inputClass} disabled={saving} />
                    </div>

                    <div>
                        <label className={labelClass}>Blurb (optional; 3 lines show, the rest unfolds on hover)</label>
                        <textarea value={form.blurb} onChange={(e) => set('blurb', e.target.value)} rows={3} className={inputClass} disabled={saving} />
                    </div>

                    {form.kind === 'update' && (
                        <div>
                            <label className={labelClass}>Changelog (optional; one change per line)</label>
                            <textarea value={form.changelog} onChange={(e) => set('changelog', e.target.value)} rows={4} className={inputClass} disabled={saving} />
                        </div>
                    )}

                    {form.kind === 'other' && (
                        <div>
                            <label className={labelClass}>Link (optional; clicking the card opens it)</label>
                            <input type="url" value={form.link} onChange={(e) => set('link', e.target.value)} placeholder="https://…" className={inputClass} disabled={saving} />
                        </div>
                    )}

                    <div>
                        <label className={labelClass}>
                            Image (optional; any PNG/JPEG, resized to {IMAGE_MAX_WIDTH}px wide and cropped to fill the card)
                        </label>
                        {shownImage && (
                            <div className="mb-2 flex items-end gap-3">
                                {/* Same box and crop as the plugin card */}
                                <img src={shownImage} alt="News entry" className="w-[122px] h-[72px] object-cover rounded-md border border-gray-200" />
                                <button
                                    onClick={() => setForm(prev => ({ ...prev, imageUrl: null, imageBlob: null }))}
                                    className="text-xs text-red-600 hover:underline"
                                    disabled={saving}
                                >
                                    Remove image
                                </button>
                            </div>
                        )}
                        <input
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={(e) => handleImageChange(e.target.files?.[0] || null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            disabled={saving}
                        />
                    </div>

                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={form.public} onChange={(e) => set('public', e.target.checked)} disabled={saving} />
                        Public (unchecked = draft, visible to admins only)
                    </label>

                    {validationError && (
                        <p className="text-red-600 font-medium text-xs">{validationError}</p>
                    )}

                    <div className="flex gap-3">
                        <button
                            onClick={handleSave}
                            disabled={saving || !!validationError}
                            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium ${saving || validationError
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            onClick={() => setEditingId(null)}
                            disabled={saving}
                            className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {entries.length === 0 ? (
                <p className="text-gray-500 text-sm">No news entries yet.</p>
            ) : (
                <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    {listedEntries.map(({ entry, date }) => (
                        <div key={entry.id} className="flex items-center gap-4 p-3">
                            {entry.imageUrl ? (
                                <img src={entry.imageUrl} alt="" className="w-[61px] h-[36px] object-cover rounded shrink-0" />
                            ) : (
                                <div className="w-[61px] h-[36px] rounded bg-gray-200 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">{entry.title.replace('\n', ' ')}</p>
                                <p className="text-xs text-gray-500">
                                    {date ? formatShortDate(date) : <span className="text-red-600 font-medium">No date — hidden in the plugin</span>}
                                    {' · '}{KIND_LABELS[entry.kind]}
                                    {entry.kind === 'pack' && <>: {packName(entry.packId)}</>}
                                    {entry.kind === 'update' && <> {entry.version}</>}
                                    {entry.kind === 'other' && entry.link && <> · {entry.link}</>}
                                </p>
                            </div>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${entry.public ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {entry.public ? 'Public' : 'Draft'}
                            </span>
                            <button
                                onClick={() => startEditing(entry)}
                                disabled={saving}
                                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleDelete(entry)}
                                disabled={saving}
                                className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                            >
                                Delete
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default NewsEditor;
